import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AdminDashboard from 'layouts/AdminDashboard';
import {
  endTrial,
  extendTrial,
  grantTrial,
  listTrials,
  TrialRecord,
} from './TrialAdminService';

// ─── constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

// Features that are lost when a trial expires (for the preview)
const TIER_FEATURES: Record<string, string[]> = {
  free: ['money.snapshot', 'money.ratio', 'money.transactions'],
  plus: [
    'money.snapshot',
    'money.ratio',
    'money.transactions',
    'money.coach',
    'money.forecast',
    'money.csv_import',
    'goals.core',
  ],
  family: [
    'money.snapshot',
    'money.ratio',
    'money.transactions',
    'money.coach',
    'money.forecast',
    'money.csv_import',
    'goals.core',
    'household.core',
  ],
  business: [
    'money.snapshot',
    'money.ratio',
    'money.transactions',
    'money.coach',
    'money.forecast',
    'money.csv_import',
    'goals.core',
    'household.core',
    'business.core',
  ],
};

const FREE_FEATURES = new Set(TIER_FEATURES.free);

// ─── helpers ──────────────────────────────────────────────────────────────────

function stateColor(
  state: string,
): 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' {
  switch (state) {
    case 'trialing':
      return 'info';
    case 'premium':
      return 'success';
    case 'expired':
    case 'cancelled':
      return 'error';
    case 'needs_attention':
      return 'warning';
    default:
      return 'default';
  }
}

function tierColor(
  tier: string,
): 'default' | 'primary' | 'secondary' | 'success' {
  switch (tier) {
    case 'plus':
      return 'primary';
    case 'family':
      return 'secondary';
    case 'business':
      return 'success';
    default:
      return 'default';
  }
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / 86_400_000);
}

// ─── ExpiryPreview ────────────────────────────────────────────────────────────

function ExpiryPreview({ tier }: { tier: string }) {
  const features = TIER_FEATURES[tier] ?? TIER_FEATURES.free;
  const losing = features.filter((f) => !FREE_FEATURES.has(f));
  const keeping = features.filter((f) => FREE_FEATURES.has(f));
  if (losing.length === 0) return null;
  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        bgcolor: 'warning.50',
        border: '1px solid',
        borderColor: 'warning.200',
        borderRadius: 1,
      }}
    >
      <Typography variant="caption" fontWeight={700} color="warning.dark">
        On expiry the user will lose access to:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        {losing.map((f) => (
          <Chip key={f} label={f} size="small" color="warning" />
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        They will retain: {keeping.join(', ')}
      </Typography>
    </Box>
  );
}

// ─── GrantDialog ──────────────────────────────────────────────────────────────

interface GrantDialogProps {
  open: boolean;
  email: string;
  onClose: () => void;
  onSaved: (t: TrialRecord) => void;
}

function GrantDialog({ open, email, onClose, onSaved }: GrantDialogProps) {
  const [tier, setTier] = useState('plus');
  const [mode, setMode] = useState<'days' | 'date'>('days');
  const [durationDays, setDurationDays] = useState('14');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const reset = () => {
    setTier('plus');
    setMode('days');
    setDurationDays('14');
    setEndDate('');
    setReason('');
    setErr('');
    setSaving(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = async () => {
    if (!reason.trim()) {
      setErr('Reason is required.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const body =
        mode === 'days'
          ? { tier, durationDays: parseInt(durationDays, 10), reason }
          : { tier, endDate: new Date(endDate).toISOString().replace('.000', ''), reason };
      const res = await grantTrial(email, body);
      onSaved(res.data);
      handleClose();
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? 'Failed to grant trial.');
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Grant Trial — {email}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        {err && <Alert severity="error">{err}</Alert>}

        <FormControl fullWidth size="small">
          <InputLabel>Tier</InputLabel>
          <Select value={tier} label="Tier" onChange={(e) => setTier(e.target.value)}>
            <MenuItem value="plus">Plus</MenuItem>
            <MenuItem value="family">Family</MenuItem>
            <MenuItem value="business">Business</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>Duration type</InputLabel>
          <Select
            value={mode}
            label="Duration type"
            onChange={(e) => setMode(e.target.value as 'days' | 'date')}
          >
            <MenuItem value="days">Number of days</MenuItem>
            <MenuItem value="date">Specific end date</MenuItem>
          </Select>
        </FormControl>

        {mode === 'days' ? (
          <TextField
            label="Days"
            type="number"
            size="small"
            value={durationDays}
            onChange={(e) => setDurationDays(e.target.value)}
            inputProps={{ min: 1 }}
          />
        ) : (
          <TextField
            label="End date"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        )}

        <TextField
          label="Reason (required)"
          multiline
          rows={2}
          size="small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        <ExpiryPreview tier={tier} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Grant Trial'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── ExtendDialog ─────────────────────────────────────────────────────────────

interface ExtendDialogProps {
  open: boolean;
  trial: TrialRecord | null;
  onClose: () => void;
  onSaved: (t: TrialRecord) => void;
}

function ExtendDialog({ open, trial, onClose, onSaved }: ExtendDialogProps) {
  const [newEndDate, setNewEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (trial?.trialEndDate) {
      setNewEndDate(trial.trialEndDate.substring(0, 10));
    }
  }, [trial]);

  const handleClose = () => {
    setNewEndDate('');
    setReason('');
    setErr('');
    setSaving(false);
    onClose();
  };

  const handleSave = async () => {
    if (!trial) return;
    if (!reason.trim()) {
      setErr('Reason is required.');
      return;
    }
    if (!newEndDate) {
      setErr('New end date is required.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const isoDate = new Date(newEndDate + 'T23:59:59Z').toISOString().replace('.000', '');
      const res = await extendTrial(trial.userEmail, { newEndDate: isoDate, reason });
      onSaved(res.data);
      handleClose();
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? 'Failed to extend trial.');
      setSaving(false);
    }
  };

  if (!trial) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Extend Trial — {trial.userEmail}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        {err && <Alert severity="error">{err}</Alert>}

        <Typography variant="body2" color="text.secondary">
          Current end date: <strong>{fmtDate(trial.trialEndDate)}</strong>
        </Typography>

        {trial.managingPlatform && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            This subscription is managed by{' '}
            <strong>{trial.managingPlatform}</strong>. Only the Docket trial
            end date will be updated — the provider billing date is unchanged.
          </Alert>
        )}

        <TextField
          label="New end date"
          type="date"
          size="small"
          value={newEndDate}
          onChange={(e) => setNewEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Reason (required)"
          multiline
          rows={2}
          size="small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── EndDialog ────────────────────────────────────────────────────────────────

interface EndDialogProps {
  open: boolean;
  trial: TrialRecord | null;
  onClose: () => void;
  onSaved: (t: TrialRecord) => void;
}

function EndDialog({ open, trial, onClose, onSaved }: EndDialogProps) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleClose = () => {
    setReason('');
    setErr('');
    setSaving(false);
    onClose();
  };

  const handleSave = async () => {
    if (!trial) return;
    if (!reason.trim()) {
      setErr('Reason is required.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const res = await endTrial(trial.userEmail, { reason });
      onSaved(res.data);
      handleClose();
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? 'Failed to end trial.');
      setSaving(false);
    }
  };

  if (!trial) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>End Trial Early — {trial.userEmail}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        {err && <Alert severity="error">{err}</Alert>}

        <Alert severity="warning">
          The trial will be moved to <strong>expired</strong>. The user will
          immediately lose access to paid features. Their data will be
          preserved.
        </Alert>

        <ExpiryPreview tier={trial.tier} />

        <TextField
          label="Reason (required)"
          multiline
          rows={2}
          size="small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={18} /> : 'End Trial'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── TrialAdminPage ───────────────────────────────────────────────────────────

const FILTER_TABS = [
  { label: 'All', state: undefined, endingSoonDays: undefined },
  { label: 'Active', state: 'trialing', endingSoonDays: undefined },
  { label: 'Ending ≤7 days', state: undefined, endingSoonDays: 7 },
  { label: 'Expired', state: 'expired', endingSoonDays: undefined },
  { label: 'Converted', state: 'premium', endingSoonDays: undefined },
  { label: 'Cancelled', state: 'cancelled', endingSoonDays: undefined },
];

const TrialAdminPage = (): JSX.Element => {
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [trials, setTrials] = useState<TrialRecord[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // Dialogs
  const [grantEmail, setGrantEmail] = useState<string | null>(null);
  const [extendTarget, setExtendTarget] = useState<TrialRecord | null>(null);
  const [endTarget, setEndTarget] = useState<TrialRecord | null>(null);

  const { state: stateFilter, endingSoonDays } = FILTER_TABS[tab];

  const load = useCallback(() => {
    setLoading(true);
    setErr('');
    listTrials(page - 1, PAGE_SIZE, stateFilter, endingSoonDays)
      .then((res) => {
        setTrials(res.data.content);
        setTotalPages(res.data.totalPages);
      })
      .catch(() => setErr('Failed to load trials.'))
      .finally(() => setLoading(false));
  }, [page, stateFilter, endingSoonDays]);

  useEffect(() => {
    load();
  }, [load]);

  const handleTabChange = (_: React.SyntheticEvent, v: number) => {
    setTab(v);
    setPage(1);
  };

  const handleSaved = (updated: TrialRecord) => {
    setTrials((prev) =>
      prev.map((t) =>
        t.subscriptionId === updated.subscriptionId ? updated : t,
      ),
    );
    // Reload to pick up filter changes
    load();
  };

  return (
    <AdminDashboard>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Trial Administration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Grant, extend and end trials. All actions are audited. Trial end
          dates are independent of provider billing dates.
        </Typography>

        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          {FILTER_TABS.map((f, i) => (
            <Tab key={i} label={f.label} />
          ))}
        </Tabs>

        {err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {err}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Tier</TableCell>
                  <TableCell>Trial end</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trials.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No records match this filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {trials.map((t) => {
                  const days = daysUntil(t.trialEndDate);
                  return (
                    <TableRow key={t.subscriptionId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {t.userEmail}
                        </Typography>
                        {t.userName.trim() && (
                          <Typography variant="caption" color="text.secondary">
                            {t.userName.trim()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t.state}
                          size="small"
                          color={stateColor(t.state)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t.tier}
                          size="small"
                          color={tierColor(t.tier)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {t.trialEndDate ? (
                          <Box>
                            <Typography variant="body2">
                              {fmtDate(t.trialEndDate)}
                            </Typography>
                            {days != null && (
                              <Typography
                                variant="caption"
                                color={
                                  days < 0
                                    ? 'error.main'
                                    : days <= 3
                                      ? 'warning.main'
                                      : 'text.secondary'
                                }
                              >
                                {days < 0
                                  ? `${Math.abs(days)}d overdue`
                                  : days === 0
                                    ? 'Today'
                                    : `${days}d remaining`}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {t.managingPlatform ? (
                          <Chip
                            label={t.managingPlatform}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            admin
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDate(t.updatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}
                        >
                          <Tooltip title="Grant / reset trial">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setGrantEmail(t.userEmail)}
                            >
                              Grant
                            </Button>
                          </Tooltip>
                          {t.state === 'trialing' && (
                            <>
                              <Tooltip title="Change trial end date">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={() => setExtendTarget(t)}
                                >
                                  Extend
                                </Button>
                              </Tooltip>
                              <Tooltip title="End trial early">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => setEndTarget(t)}
                                >
                                  End
                                </Button>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Container>

      {/* Dialogs */}
      <GrantDialog
        open={grantEmail !== null}
        email={grantEmail ?? ''}
        onClose={() => setGrantEmail(null)}
        onSaved={handleSaved}
      />
      <ExtendDialog
        open={extendTarget !== null}
        trial={extendTarget}
        onClose={() => setExtendTarget(null)}
        onSaved={handleSaved}
      />
      <EndDialog
        open={endTarget !== null}
        trial={endTarget}
        onClose={() => setEndTarget(null)}
        onSaved={handleSaved}
      />
    </AdminDashboard>
  );
};

export default TrialAdminPage;
